/* eslint-disable max-lines */
/* eslint-disable max-len */

import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths
} from "date-fns";
import React, { useState } from "react";

export type SelectedDateType = {
  date: Date;
  type: "single" | "range";
  rangeId?: string;
};

interface EnhancedDayPickerProps {
  selectedDates: SelectedDateType[];
  onChange: (dates: SelectedDateType[]) => void;
}

const EnhancedDayPicker: React.FC<EnhancedDayPickerProps> = ({
  selectedDates,
  onChange
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isRangeSelection, setIsRangeSelection] = useState(false);
  const [rangeStart, setRangeStart] = useState<Date | null>(null);

  const onDateClick = (day: Date) => {
    if (isRangeSelection) {
      if (!rangeStart) {
        setRangeStart(day);
      } else {
        const start = rangeStart < day ? rangeStart : day;
        const end = rangeStart < day ? day : rangeStart;
        const rangeId = Math.random().toString(36).substr(2, 9);

        // Get all dates that are completely outside our new range
        const datesOutsideRange = selectedDates.filter((d) => {
          const dateTime = d.date.getTime();
          return dateTime < start.getTime() || dateTime > end.getTime();
        });

        // Create a single continuous range
        const newDates = eachDayOfInterval({ start, end }).map((date) => ({
          date,
          type: "range" as const,
          rangeId
        }));

        // Combine the dates outside our range with our new range
        onChange([...datesOutsideRange, ...newDates]);
        setRangeStart(null);
      }
    } else {
      // Check if the day is part of any range
      const isInRange = selectedDates.some(
        (d) => d.type === "range" && isSameDay(d.date, day)
      );

      if (isInRange) {
        // Get the rangeId of the clicked date
        const clickedRangeId = selectedDates.find(
          (d) => d.type === "range" && isSameDay(d.date, day)
        )?.rangeId;

        // Separate dates into the current range and other dates
        const currentRangeDates = selectedDates
          .filter((d) => d.rangeId === clickedRangeId)
          .map((d) => d.date)
          .sort((a, b) => a.getTime() - b.getTime());

        const otherDates = selectedDates.filter((d) => d.rangeId !== clickedRangeId);

        // Find the index of the clicked date in the sorted range
        const clickedIndex = currentRangeDates.findIndex((d) => isSameDay(d, day));

        // Split the range into before and after the clicked date
        const beforeDates = currentRangeDates.slice(0, clickedIndex);
        const afterDates = currentRangeDates.slice(clickedIndex + 1);

        const processedDates: SelectedDateType[] = [...otherDates];

        // Process dates before clicked date
        if (beforeDates.length > 1) {
          const beforeRangeId = Math.random().toString(36).substr(2, 9);
          beforeDates.forEach((date) => {
            processedDates.push({
              date,
              type: "range",
              rangeId: beforeRangeId
            });
          });
        } else if (beforeDates.length === 1) {
          processedDates.push({ date: beforeDates[0], type: "single" });
        }

        // Process dates after clicked date
        if (afterDates.length > 1) {
          const afterRangeId = Math.random().toString(36).substr(2, 9);
          afterDates.forEach((date) => {
            processedDates.push({
              date,
              type: "range",
              rangeId: afterRangeId
            });
          });
        } else if (afterDates.length === 1) {
          processedDates.push({ date: afterDates[0], type: "single" });
        }

        onChange(processedDates);
      } else {
        const exists = selectedDates.some(
          (d) => isSameDay(d.date, day) && d.type === "single"
        );
        if (exists) {
          onChange(
            selectedDates.filter(
              (d) => !(isSameDay(d.date, day) && d.type === "single")
            )
          );
        } else {
          onChange([...selectedDates, { date: day, type: "single" }]);
        }
      }
    }
  };

  // Optional: a helper to remove a date (or an entire range) from the selection.
  const removeDate = (
    date: Date,
    type: "single" | "range",
    rangeId?: string
  ) => {
    if (type === "range" && rangeId) {
      onChange(selectedDates.filter((d) => d.rangeId !== rangeId));
    } else {
      onChange(
        selectedDates.filter(
          (d) => !(isSameDay(d.date, date) && d.type === type)
        )
      );
    }
  };

  // Render a simple header with month navigation.
  const renderHeader = (monthDate: Date) => (
    <div className="flex justify-between items-center mb-4 px-4">
      {monthDate === currentMonth && (
        <button
          type="button"
          className="text-3xl text-[#f05a2d] hover:text-[#d94d26] font-semibold px-2"
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
        >
          ‹
        </button>
      )}
      <h2 className="text-lg font-semibold text-gray-800">{format(monthDate, "MMMM yyyy")}</h2>
      {monthDate === currentMonth && (
        <button
          type="button"
          className="text-3xl text-[#f05a2d] hover:text-[#d94d26] font-semibold px-2"
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
        >
          ›
        </button>
      )}
    </div>
  );

  // Render weekday names.
  const renderDays = () => {
    const dateFormat = "EEEEEE";
    const days = [];
    const startDate = startOfWeek(currentMonth);
    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={i} className="font-medium text-center text-gray-600 text-xs">
          {format(addDays(startDate, i), dateFormat)}
        </div>
      );
    }
    return <div className="grid grid-cols-7 gap-y-1 mb-1">{days}</div>;
  };

  // Render all days in the calendar view.
  const renderCells = (monthDate: Date) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const formattedDate = format(day, "d");
        const disabled = !isSameMonth(day, monthStart);
        const isSelected = selectedDates.some((d) => isSameDay(d.date, day));
        const isRangeStart = rangeStart && isSameDay(rangeStart, day);
        days.push(
          <div
            key={day.toString()}
            className={`
              flex items-center justify-center h-8 w-8 mx-0 text-sm
              ${disabled ? "text-gray-400" : "hover:bg-[#fef7f5] cursor-pointer"}
              ${isSelected || isRangeStart ? "bg-[#f05a2d] text-white hover:bg-[#d94d26]" : ""}
              rounded-full
              transition-colors duration-200
            `}
            onClick={() => !disabled && onDateClick(cloneDay)}
          >
            {formattedDate}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7">
          {days}
        </div>
      );
      days = [];
    }
    return <div className="mb-2">{rows}</div>;
  };

  // Render a list of selected dates (grouping range selections).
  const renderSelectedDates = () => {
    const singles = selectedDates.filter((d) => d.type === "single");
    const ranges = selectedDates.filter((d) => d.type === "range");
    const rangeGroups = ranges.reduce((acc, curr) => {
      if (!acc[curr.rangeId!]) acc[curr.rangeId!] = [];
      acc[curr.rangeId!].push(curr);
      return acc;
    }, {} as Record<string, SelectedDateType[]>);

    if (singles.length === 0 && Object.keys(rangeGroups).length === 0) {
      return null;
    }

    return (
      <div className="mt-4 w-[400px]">
        <p className="text-sm font-medium text-gray-700 mb-1">Datat e zgjedhura:</p>
        <div className="flex flex-wrap gap-2">
          {singles.map((d, i) => (
            <span
              key={i}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#fef7f5] text-[#f05a2d]"
            >
              {format(d.date, "dd/MM/yyyy")}
              <button
                type="button"
                onClick={() => removeDate(d.date, "single")}
                className="ml-1 inline-flex items-center p-0.5 text-[#f05a2d] hover:text-[#d94d26]"
              >
                ×
              </button>
            </span>
          ))}
          {Object.values(rangeGroups).map((range, i) => {
            const start = range[0].date;
            const end = range[range.length - 1].date;
            return (
              <span
                key={i}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#fef7f5] text-[#f05a2d]"
              >
                {format(start, "dd/MM/yyyy")} - {format(end, "dd/MM/yyyy")}
                <button
                  type="button"
                  onClick={() => removeDate(start, "range", range[0].rangeId)}
                  className="ml-1 inline-flex items-center p-0.5 text-[#f05a2d] hover:text-[#d94d26]"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-md mx-auto p-2 custom-calendar">
      <div className="mb-3">
        <button
          type="button"
          className={`mr-2 px-3 py-1.5 text-sm rounded-full transition-colors duration-200 ${
            isRangeSelection
              ? "bg-[#f05a2d] text-white"
              : "bg-gray-200 text-gray-800"
          }`}
          onClick={() => {
            setIsRangeSelection(true);
            setRangeStart(null);
          }}
        >
          Zgjedh interval
        </button>
        <button
          type="button"
          className={`px-3 py-1.5 text-sm rounded-full transition-colors duration-200 ${
            !isRangeSelection
              ? "bg-[#f05a2d] text-white"
              : "bg-gray-200 text-gray-800"
          }`}
          onClick={() => {
            setIsRangeSelection(false);
            setRangeStart(null);
          }}
        >
          Zgjedh datë
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-3">
          {renderHeader(currentMonth)}
          {renderDays()}
          {renderCells(currentMonth)}
        </div>
      </div>
      {renderSelectedDates()}
    </div>
  );
};

export default EnhancedDayPicker;
